import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import { LoadingButton } from "@mui/lab";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";

const PricingEditor = () => {
  const { data, isLoading, isError, error } = useApiEp(`/root/get-pricing`);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newPricing, setNewPricing] = useState({
    country: "",
    countryCode: "",
    phoneCode: "",
    marketing: "",
    utility: "",
    authentication: "",
    notes: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (data?.rates) {
      setRows(data.rates);
    }
  }, [data]);

  const validatePrice = (value) => {
    if (value === "") return "Price cannot be empty";
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return "Price must be a non-negative number";
    if (!value.toString().match(/^\d*\.?\d{0,4}$/))
      return "Price must have up to 4 decimal places";
    return "";
  };

  const handleCellEdit = (newRow, oldRow) => {
    const updatedField = Object.keys(newRow).find(
      (key) => newRow[key] !== oldRow[key]
    );

    // Validate pricing fields
    if (
      updatedField === "marketing" ||
      updatedField === "utility" ||
      updatedField === "authentication"
    ) {
      const error = validatePrice(newRow[updatedField]);

      if (error) {
        setSnackbar({
          open: true,
          message: error,
          severity: "error",
        });
        return oldRow; // Revert to old value
      }
    }

    // Update the rows state
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === newRow.id ? newRow : row))
    );

    return newRow;
  };

  const handleProcessRowUpdateError = (error) => {
    setSnackbar({
      open: true,
      message: "Failed to update row",
      severity: "error",
    });
  };

  const handleAddNew = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setNewPricing({
      country: "",
      countryCode: "",
      phoneCode: "",
      marketing: "",
      utility: "",
      authentication: "",
      notes: "",
    });
  };

  const validateNewPricing = () => {
    if (!newPricing.country.trim()) return "Country is required";
    if (!newPricing.countryCode.trim()) return "Country Code is required";
    if (!newPricing.phoneCode.trim()) return "Phone Code is required";

    const marketingError = validatePrice(newPricing.marketing);
    if (marketingError) return `Marketing: ${marketingError}`;

    const utilityError = validatePrice(newPricing.utility);
    if (utilityError) return `Utility: ${utilityError}`;

    const authError = validatePrice(newPricing.authentication);
    if (authError) return `Authentication: ${authError}`;

    return "";
  };

  const handleAddPricing = () => {
    const validationError = validateNewPricing();
    if (validationError) {
      setSnackbar({
        open: true,
        message: validationError,
        severity: "error",
      });
      return;
    }

    const newRow = {
      id: Math.max(...rows.map((r) => r.id), 0) + 1,
      country: newPricing.country.trim(),
      countryCode: newPricing.countryCode.trim().toUpperCase(),
      phoneCode: newPricing.phoneCode.trim(),
      marketing: parseFloat(newPricing.marketing),
      utility: parseFloat(newPricing.utility),
      authentication: parseFloat(newPricing.authentication),
      notes: newPricing.notes.trim(),
    };

    setRows((prev) => [...prev, newRow]);
    handleDialogClose();
    setSnackbar({
      open: true,
      message: "New pricing added successfully! Don't forget to save.",
      severity: "success",
    });
  };

  const handleDeleteRow = (id) => {
    if (window.confirm("Are you sure you want to delete this pricing item?")) {
      setRows((prev) => prev.filter((row) => row.id !== id));
      setSnackbar({
        open: true,
        message: "Pricing item deleted. Don't forget to save changes.",
        severity: "info",
      });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = { pricingList: rows };
      const response = await axios.post(
        `${API_ENDPOINT}/root/save-pricing`,
        payload,
        getHeader()
      );

      if (response.data.rates) {
        setRows(response.data.rates);
      }

      toast.success(response.data.message || "Prices saved successfully!");
      setSnackbar({
        open: true,
        message: "Prices saved successfully!",
        severity: "success",
      });
    } catch (error) {
      toastError(error);
      setSnackbar({
        open: true,
        message: getErrorResponse(error),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const columns = [
    {
      field: "country",
      headerName: "Country",
      width: 150,
      editable: true,
      renderCell: (params) => (
        <Tooltip title={params.row.notes || "No additional notes"} arrow>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "countryCode",
      headerName: "Country Code",
      width: 120,
      editable: true,
    },
    {
      field: "phoneCode",
      headerName: "Phone Code",
      width: 120,
      editable: true,
    },
    {
      field: "marketing",
      headerName: "Marketing (INR)",
      width: 150,
      editable: true,
      type: "number",
      renderHeader: () => (
        <Tooltip title="Cost per marketing conversation in INR" arrow>
          <span>Marketing (INR)</span>
        </Tooltip>
      ),
    },
    {
      field: "utility",
      headerName: "Utility (INR)",
      width: 150,
      editable: true,
      type: "number",
      renderHeader: () => (
        <Tooltip title="Cost per utility conversation in INR" arrow>
          <span>Utility (INR)</span>
        </Tooltip>
      ),
    },
    {
      field: "authentication",
      headerName: "Authentication (INR)",
      width: 150,
      editable: true,
      type: "number",
      renderHeader: () => (
        <Tooltip title="Cost per authentication conversation in INR" arrow>
          <span>Authentication (INR)</span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Delete this pricing item" arrow>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteRow(params.row.id)}
          >
            Delete
          </Button>
        </Tooltip>
      ),
    },
  ];

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", my: 4, p: 2 }}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            WhatsApp Business API Pricing Editor (INR)
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add New Pricing
          </Button>
        </Box>

        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            processRowUpdate={handleCellEdit}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            sx={{
              "& .MuiDataGrid-cell": { py: 1 },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
            }}
          />
        </Box>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Tooltip title="Save all changes to pricing" arrow>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={saving}
              loadingPosition="start"
              startIcon={<SaveIcon />}
              onClick={handleSave}
            >
              Save All Changes
            </LoadingButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Add New Pricing Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Pricing Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Country"
                value={newPricing.country}
                onChange={(e) =>
                  setNewPricing({ ...newPricing, country: e.target.value })
                }
                required
                placeholder="e.g., United States"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Country Code"
                value={newPricing.countryCode}
                onChange={(e) =>
                  setNewPricing({
                    ...newPricing,
                    countryCode: e.target.value.toUpperCase(),
                  })
                }
                required
                placeholder="e.g., US"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone Code"
                value={newPricing.phoneCode}
                onChange={(e) =>
                  setNewPricing({ ...newPricing, phoneCode: e.target.value })
                }
                required
                placeholder="e.g., +1"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Marketing (INR)"
                type="number"
                value={newPricing.marketing}
                onChange={(e) =>
                  setNewPricing({ ...newPricing, marketing: e.target.value })
                }
                required
                inputProps={{ step: "0.0001", min: 0 }}
                helperText="Cost per marketing conversation"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Utility (INR)"
                type="number"
                value={newPricing.utility}
                onChange={(e) =>
                  setNewPricing({ ...newPricing, utility: e.target.value })
                }
                required
                inputProps={{ step: "0.0001", min: 0 }}
                helperText="Cost per utility conversation"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Authentication (INR)"
                type="number"
                value={newPricing.authentication}
                onChange={(e) =>
                  setNewPricing({
                    ...newPricing,
                    authentication: e.target.value,
                  })
                }
                required
                inputProps={{ step: "0.0001", min: 0 }}
                helperText="Cost per authentication conversation"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                value={newPricing.notes}
                onChange={(e) =>
                  setNewPricing({ ...newPricing, notes: e.target.value })
                }
                multiline
                rows={2}
                placeholder="Add any additional notes"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleAddPricing}
            variant="contained"
            color="primary"
          >
            Add Pricing
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PricingEditor;
